import React, {useState, useEffect, useRef} from 'react';
import {ObjectId, Decimal128} from 'bson';
import {View, Text, ScrollView, Dimensions} from 'react-native';
import {useToast} from 'react-native-toast-notifications';
import {DynamicPageStyleSheet} from '../../Styles/DynamicPageStyles';
import {ControlTypes} from '../../Constants/ControlTypes';
import DynamicTextArea from '../DynamicControls/DynamicTextArea';
import {
  AttachmentConfig,
  GroupSelectConfig,
  LabelConfig,
  RatingConfig,
  SegmentConfig,
  TagItemConfig,
  TextBoxConfig,
  TextAreaConfig,
  LinkRecordConfig,
  ReportDescriptionConfig,
  SelectItemConfig,
  TipConfig,
  LinkedDropDownsConfig,
  RiskRatingConfig,
  DatePickerConfig,
  TimePickerConfig,
  FacePileConfig,
  AutocompleteConfig,
  AutocompleteExtendedConfig,
  RadioConfig,
  CheckboxGroupConfig,
  SignatureConfig,
  SignatureListConfig,
  PeopleListConfig,
  EidaDescriptionConfig,
  LinkButtonConfig,
  LinkTextConfig,
  LocationMapConfig,
} from '../../Types/ControlConfigTypes';
import DynamicLabel from '../DynamicControls/DynamicLabel';
import DynamicDatePicker from '../DynamicControls/DynamicDatePicker';
import DynamicTimePicker from '../DynamicControls/DynamicTimePicker';
import DynamicSelect from '../DynamicControls/DynamicSelect';
import DynamicTagItems from '../DynamicControls/DynamicTagItems';
import {SubmissionAnswerDTO} from '../../Types/DtoTypes';
import DynamicGroupSelector from '../DynamicControls/DynamicGroupSelector';
import DynamicRating from '../DynamicControls/DynamicRating';
import DynamicLocationMap from '../DynamicControls/DynamicLocationMap';
import DynamicAttachments from '../DynamicControls/DynamicAttachments';
import DynamicSegmentedControl from '../DynamicControls/DynamicSegmentedControl';
import DynamicSignature from '../DynamicControls/DynamicSignature';
import DynamicSignatureList from '../DynamicControls/DynamicSignatureList';
import DynamicLinkRecord from '../DynamicControls/DynamicLinkRecord';
import DynamicReportDescription from '../DynamicControls/DynamicReportDescription';
import DynamicCheckBox from '../DynamicControls/DynamicCheckBox';
import DynamicLinkedDropDowns from '../DynamicControls/DynamicLinkedDropDowns';
import DynamicFacepile from '../DynamicControls/DynamicFacepile';
import DynamicTextBox from '../DynamicControls/DynamicTextBox';
import DynamicAutocomplete from '../DynamicControls/DynamicAutocomplete';
import {
  TemplateVersion_pages,
  TemplateVersion_pages_controls,
  TemplateVersion_conditions,
} from '../../Models/PowerSyncModels/models';
import DynamicSubmitButton from '../DynamicControls/DynamicSubmitButton';
import DynamicTip from '../DynamicControls/DynamicTip';
import DynamicRiskRating from '../DynamicControls/DynamicRiskRating';
import {ControlsStyleSheet} from '../../Styles/Shared/Controls';
import IConditionsHelperService from '../../Services/Interfaces/IConditionsHelperService';
import ConditionsHelperService from '../../Services/ConditionsHelperService';
import DynamicRadioControl from '../DynamicControls/DynamicRadioControl';
import DynamicCheckBoxGroup from '../DynamicControls/DynamicCheckBoxGroup';
import useDynamicDefault from '../../Hooks/useDynamicDefault';
import DynamicAutocompleteExtended from '../DynamicControls/DynamicAutocompleteExtended';
import {DEFAULT_SOURCE_KEY} from '../../Constants/AppConstants';
import DynamicPeopleList from '../DynamicControls/DynamicPeopleList';
import DynamicEidaDescription from '../DynamicControls/DynamicEidaDescription';
import DynamicLinkButton from '../DynamicControls/DynamicLinkButton';
import DynamicLinkText from '../DynamicControls/DynamicLinkText';
import {SubmissionStatuses} from '../../Constants/SubmissionStatuses';

type DynamicPageProps = {
  navigation: any;
  submissionId: string;
  submissionSQLServerId: string;
  onChange?: (
    controlId: number | undefined,
    controlTypeId: number | undefined,
    value: string,
    isValid: boolean,
    metaDataKey: string | null,
    metaDataValue: string | null,
  ) => void;
  updateMetaData?: (key: string, value: string) => void;
  submitForm: () => void;
  validateAllPages?: () => boolean;
  answers: SubmissionAnswerDTO[];
  page: TemplateVersion_pages;
  pageNumber: number;
  conditions?: TemplateVersion_conditions[];
  controlHash?: Map<number, TemplateVersion_pages_controls>; //Conditions across many pages
  disabled: boolean;
  submissionStatus?: string;
  validations: {
    controlId: number;
    Label: string;
    page: number;
    isValid: boolean;
  }[];
  showErrors?: boolean;
  externalView?: boolean;
  setIsLoading: (loading: boolean) => void;
  formVersion: number;
  setScrollToBottom?: (func: () => void) => void;
};

const DynamicPage = (props: DynamicPageProps): React.ReactElement => {
  const conditionsHelper: IConditionsHelperService =
    new ConditionsHelperService();

  const [focusedControlId, setFocusedControlId] = useState<number | undefined>(
    undefined,
  );

  const [controlHash, setControlHash] = useState<
    Map<number, TemplateVersion_pages_controls>
  >(new Map<number, TemplateVersion_pages_controls>());

  const {getDefaultValue} = useDynamicDefault();
  const toast = useToast();

  useEffect(() => {
    if(props.setScrollToBottom)
      props.setScrollToBottom(() => {
        scrollToBottom();
      });
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTo({y: 0, animated: false});

    let newControlHash: Map<number, TemplateVersion_pages_controls> = new Map<
      number,
      TemplateVersion_pages_controls
    >();

    for (let i = 0; i < props.page.controls!.length; i++) {
      newControlHash.set(
        props.page.controls![i].controlId!,
        props.page.controls![i],
      );
    }

    setControlHash(newControlHash);
  }, [props.pageNumber]);

  const onSubmit = () => {
    if (props.validateAllPages && !props.validateAllPages()) return;

    props.submitForm();
  };
  const onChange = (
    controlId: number | undefined,
    controlTypeId: number | undefined,
    value: string,
    isValid: boolean,
    isRequired: boolean,
    metaDataKey: string | null,
    metaDataValue: string | null,
  ) => {
    if (props.onChange) {
      if (
        props.submissionStatus?.toUpperCase() !==
        SubmissionStatuses.DRAFT.toUpperCase()
      ) {
        const submissionAnswer = props.answers.find(
          x => x.controlId == controlId,
        );

        let answer: string | undefined = undefined;
        if (submissionAnswer && submissionAnswer.answer)
          answer = submissionAnswer.answer;

        if (isRequired && !value && answer) {
          toast.show(
            'You cannot delete required fields from a submitted report.',
            {
              type: 'danger',
            },
          );

          return;
        }
      }

      props.onChange(
        controlId,
        controlTypeId,
        value,
        isValid,
        metaDataKey,
        metaDataValue,
      );
    }
  };

  const getFormErrors = (): string => {
    let errors = '';

    let hasErrors = props.validations.some(v => !v.isValid);
    if (hasErrors) {
      errors = 'You have some missing/invalid field values:\n';

      props.validations
        .filter(v => !v.isValid)
        .forEach(v => {
          if (v.Label) errors += '\t•' + v.Label + '\n';
        });
    }

    return errors;
  };

  function setDefault(
    answer: string | undefined,
    sourceKey: string,
    control: TemplateVersion_pages_controls,
    metaDataKey: string | null,
  ) {
    if (answer == undefined && sourceKey) {
      answer = getDefaultValue(sourceKey);
      if (answer && props.onChange)
        props.onChange(
          control.controlId,
          control.controlTypeId,
          answer,
          true,
          metaDataKey,
          answer,
        );
    }

    return answer;
  }

  const getControl = (
    control: TemplateVersion_pages_controls,
    controlHash: Map<number, TemplateVersion_pages_controls>,
  ): React.ReactElement => {
    if (!controlHash.get(control.controlId!))
      return <React.Fragment key={control.controlId}></React.Fragment>;

    const submissionAnswer = props.answers.find(
      x => x.controlId == control.controlId,
    );

    let answer: string | undefined = control.value;
    if (submissionAnswer && submissionAnswer.answer)
      answer = submissionAnswer.answer;

    let visible = conditionsHelper.isControlVisible(
      control,
      props.conditions ?? [],
      props.answers,
      controlHash,
    );
    // TODO: Use "LessThan" & "GreaterThan" conditions in ConditionsHelperService
    // I guess we'll need a DynamicNumericBox with minValue & maxValue config

    if (!visible)
      return <React.Fragment key={control.controlId}></React.Fragment>;

    let disabled = conditionsHelper.isControlDisabled(
      control,
      props.conditions ?? [],
      props.answers,
      controlHash,
    );

    //Page disabled takes priority over control conditions
    if (props.disabled) disabled = true;

    switch (control.controlTypeId) {
      case ControlTypes.LABEL: {
        let config: LabelConfig = {};

        try {
          config = JSON.parse(control.config!);
        } catch (err) {
          console.error(
            'Invalid config for label ' + control.controlId?.toString(),
          );
        }

        return (
          <DynamicLabel
            key={control.controlId}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={config}
            value={control.label}
            visible={visible}
          />
        );
      }
      case ControlTypes.DATE:
        const datePickerConfig: DatePickerConfig = JSON.parse(control.config!);

        let before = conditionsHelper.getBeforeCondition(
          control,
          props.conditions ?? [],
          props.answers,
          props.controlHash!,
        );
        if (before) datePickerConfig.maxDate = before;

        let after = conditionsHelper.getAfterCondition(
          control,
          props.conditions ?? [],
          props.answers,
          props.controlHash!,
        );
        if (after) datePickerConfig.minDate = after;

        return (
          <DynamicDatePicker
            key={control.controlId}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={datePickerConfig}
            label={control.label}
            value={answer}
            visible={visible}
            disabled={disabled}
            onChange={(
              controlId,
              controlTypeId,
              value,
              isValid,
              metaDataKey,
              metaDataValue,
            ) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                datePickerConfig.required ?? false,
                metaDataKey,
                metaDataValue,
              )
            }
            showError={props.showErrors}
          />
        );
      case ControlTypes.MAP: {
        let locationMapConfig: LocationMapConfig = {};

        try {
          locationMapConfig = JSON.parse(control.config!);
        } catch (err) {
          console.error(
            'Invalid config for Location Map ' + control.controlId?.toString(),
          );
        }

        return (
          <DynamicLocationMap
            key={control.controlId}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={locationMapConfig}
            value={answer}
            onChange={(controlId, controlTypeId, value, isValid) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                locationMapConfig?.required ?? false,
                null,
                null,
              )
            }
            visible={visible}
            disabled={disabled}
            showError={props.showErrors}
          />
        );
      }
      case ControlTypes.PHOTOS_AND_ATTACHMENTS:
        const attachmentConfig: AttachmentConfig = JSON.parse(control.config!);
        return (
          <DynamicAttachments
            key={control.controlId}
            submissionId={props.submissionSQLServerId}
            attachmentsType={'submission'}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={attachmentConfig}
            value={answer}
            visible={visible}
            disabled={disabled}
            onChange={(controlId, controlTypeId, value, isValid) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                attachmentConfig.required ?? false,
                null,
                null,
              )
            }
            setIsLoading={props.setIsLoading}
            showError={props.showErrors}
            itemMaxWidth={320}
          />
        );
      case ControlTypes.RATING:
        const ratingConfig: RatingConfig = JSON.parse(control.config!);
        return (
          <DynamicRating
            key={control.controlId}
            label={control.label}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={ratingConfig}
            value={answer}
            visible={visible}
            disabled={disabled}
            onChange={(
              controlId,
              controlTypeId,
              value,
              isValid,
              metaDataKey,
              metaDataValue,
            ) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                false,
                metaDataKey,
                metaDataValue,
              )
            }
            updateMetaData={props.updateMetaData}
          />
        );
      case ControlTypes.SELECT: {
        const selectItemConfig: SelectItemConfig = JSON.parse(control.config!);

        let filter = undefined;
        if (props.controlHash)
          filter = conditionsHelper.getFilterCondition(
            control,
            props.conditions ?? [],
            props.answers,
            props.controlHash!,
          );

        return (
          <DynamicSelect
            submissionId={props.submissionId}
            key={control.controlId}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={selectItemConfig}
            label={control.label}
            value={answer}
            visible={visible}
            disabled={disabled}
            onChange={(
              controlId,
              controlTypeId,
              value,
              isValid,
              metaDataKey,
              metaDataValue,
            ) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                selectItemConfig.required ?? false,
                metaDataKey,
                metaDataValue,
              )
            }
            showError={props.showErrors}
            filter={filter}
          />
        );
      }
      case ControlTypes.TAG_ITEM: {
        const tagItemConfig: TagItemConfig = JSON.parse(control.config!);

        if (answer == undefined && tagItemConfig.defaultSourceKey)
          answer = setDefault(
            answer,
            tagItemConfig.defaultSourceKey,
            control,
            null,
          );
        return (
          <DynamicTagItems
            key={control.controlId}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={tagItemConfig}
            label={control.label}
            value={answer}
            visible={visible}
            disabled={disabled}
            onChange={(controlId, controlTypeId, value, isValid) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                tagItemConfig.required ?? false,
                null,
                null,
              )
            }
            showError={props.showErrors}
          />
        );
      }
      case ControlTypes.TEXTAREA: {
        const textAreaConfig: TextAreaConfig = JSON.parse(control.config!);
        return (
          <DynamicTextArea
            key={control.controlId}
            label={control.label}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={textAreaConfig}
            value={answer}
            visible={visible}
            disabled={disabled}
            onChange={(
              controlId,
              controlTypeId,
              value,
              isValid,
              metaDataKey,
              metaDataValue,
            ) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                textAreaConfig.required ?? false,
                metaDataKey,
                metaDataValue,
              )
            }
            showError={props.showErrors}
          />
        );
      }
      case ControlTypes.TEXTBOX:
        const textBoxConfig: TextBoxConfig = JSON.parse(control.config!);

        let autofillTextBox = undefined;
        if (props.controlHash)
          autofillTextBox = conditionsHelper.getAutofillCondition(
            control,
            props.conditions ?? [],
            props.answers,
            props.controlHash!,
          );

        if (answer == undefined && textBoxConfig.defaultSourceKey) {
          let metaDataKey: string | null = null;
          if (textBoxConfig.hasMetaData && textBoxConfig.metaDataKey)
            metaDataKey = textBoxConfig.metaDataKey;

          answer = setDefault(
            answer,
            textBoxConfig.defaultSourceKey,
            control,
            metaDataKey,
          );
        }

        return (
          <DynamicTextBox
            key={control.controlId}
            label={control.label}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            value={answer}
            config={textBoxConfig}
            visible={visible}
            disabled={disabled}
            onChange={(
              controlId,
              controlTypeId,
              value,
              isValid,
              metaDataKey,
              metaDataValue,
            ) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                textBoxConfig.required ?? false,
                metaDataKey,
                metaDataValue,
              )
            }
            autofill={autofillTextBox}
            showError={props.showErrors}
          />
        );
      case ControlTypes.TIME:
        const timePickerConfig: TimePickerConfig = JSON.parse(control.config!);
        return (
          <DynamicTimePicker
            key={control.controlId}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={timePickerConfig}
            label={control.label}
            value={answer}
            visible={visible}
            disabled={disabled}
            onChange={(
              controlId,
              controlTypeId,
              value,
              isValid,
              metaDataKey,
              metaDataValue,
            ) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                timePickerConfig.required ?? false,
                metaDataKey,
                metaDataValue,
              )
            }
            showError={props.showErrors}
          />
        );
      case ControlTypes.GROUP_SELECT:
        const groupSelectConfig: GroupSelectConfig = JSON.parse(
          control.config!,
        );
        return (
          <DynamicGroupSelector
            key={control.controlId}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={groupSelectConfig}
            label={control.label}
            value={answer}
            visible={visible}
            disabled={disabled}
            onChange={(controlId, controlTypeId, value, isValid) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                groupSelectConfig.required ?? false,
                null,
                null,
              )
            }
            showError={props.showErrors}
          />
        );
      case ControlTypes.SUBMIT:
        if (
          props.submissionStatus &&
          props.submissionStatus.toUpperCase() ===
            SubmissionStatuses.DRAFT.toUpperCase()
        )
          return (
              <Text
                style={[
                  ControlsStyleSheet.error,
                  {fontSize: 12, marginTop: 16},
                ]}>
                {getFormErrors()}
              </Text>
          );
        else return <React.Fragment key={control.controlId}></React.Fragment>;
      case ControlTypes.SEGMENTED_CONTROL:
        const segmentConfig: SegmentConfig = JSON.parse(control.config!);
        return (
          <DynamicSegmentedControl
            key={control.controlId}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={segmentConfig}
            label={control.label}
            value={answer}
            defaultValue={control.value}
            visible={visible}
            disabled={disabled}
            onChange={(
              controlId,
              controlTypeId,
              value,
              isValid,
              metaDataKey,
              metaDataValue,
            ) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                false,
                metaDataKey,
                metaDataValue,
              )
            }
          />
        );
      case ControlTypes.SIGNATURE:
        let signatureConfig: SignatureConfig | undefined = undefined;

        try {
          if (control.config) signatureConfig = JSON.parse(control.config);
        } catch (err) {
          console.error(
            'Invalid config for signature ' + control.controlId?.toString(),
          );
        }

        return (
          <DynamicSignature
            key={control.controlId}
            submissionId={props.submissionSQLServerId}
            attachmentsType={'submission'}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={signatureConfig}
            label={control.label}
            value={answer}
            visible={visible}
            disabled={disabled}
            onFocus={() => setScrollEnabled(false)}
            onBlur={() => setScrollEnabled(true)}
            onChange={(controlId, controlTypeId, value, isValid) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                signatureConfig?.required ?? false,
                null,
                null,
              )
            }
            setIsLoading={props.setIsLoading}
            showError={props.showErrors}
          />
        );
      case ControlTypes.SIGNATURE_LIST:
        let signatureListConfig: SignatureListConfig | undefined = undefined;

        try {
          if (control.config) signatureListConfig = JSON.parse(control.config);
        } catch (err) {
          console.error(
            'Invalid config for signature ' + control.controlId?.toString(),
          );
        }

        return (
          <DynamicSignatureList
            key={control.controlId}
            submissionId={props.submissionSQLServerId}
            attachmentsType={'submission'}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={signatureListConfig}
            label={control.label}
            value={answer}
            onChange={(controlId, controlTypeId, value, isValid) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                signatureListConfig?.required ?? false,
                null,
                null,
              )
            }
            setIsLoading={props.setIsLoading}
            visible={visible}
            disabled={disabled}
            showError={props.showErrors}
          />
        );
      case ControlTypes.LINK_RECORD:
        const linkRecordConfig: LinkRecordConfig = JSON.parse(control.config!);
        return (
          <DynamicLinkRecord
            key={control.controlId}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            submissionId={String(props.submissionId)}
            label={control.label}
            config={linkRecordConfig}
            navigation={props.navigation}
            visible={visible}
            disabled={disabled}
          />
        );
      case ControlTypes.REPORT_DESCRIPTION:
        const reportDescriptionConfig: ReportDescriptionConfig = JSON.parse(
          control.config!,
        );
        return (
          <DynamicReportDescription
            key={control.controlId}
            label={control.label}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={reportDescriptionConfig}
            value={answer}
            visible={visible}
            disabled={disabled}
            onChange={(controlId, controlTypeId, value, isValid) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                reportDescriptionConfig.required ?? false,
                null,
                null,
              )
            }
            showError={props.showErrors}
          />
        );
      case ControlTypes.CHECK_BOX:
        return (
          <DynamicCheckBox
            key={control.controlId}
            label={control.label}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            value={answer}
            visible={visible}
            disabled={disabled}
            onChange={(controlId, controlTypeId, value, isValid) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                false,
                null,
                null,
              )
            }
          />
        );
      case ControlTypes.FACEPILE:
        const facePileConfig: FacePileConfig = JSON.parse(control.config!);

        if (answer == undefined && facePileConfig.defaultSourceKey)
          answer = setDefault(
            answer,
            facePileConfig.defaultSourceKey,
            control,
            null,
          );

        return (
          <DynamicFacepile
            key={control.controlId}
            label={control.label}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={facePileConfig}
            navigation={props.navigation}
            value={answer}
            visible={visible}
            disabled={disabled}
            onChange={(controlId, controlTypeId, value, isValid) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                facePileConfig.required ?? false,
                null,
                null,
              )
            }
          />
        );
      case ControlTypes.LINKED_DROPDOWNS:
        const linkedDropDownsConfig: LinkedDropDownsConfig = JSON.parse(
          control.config!,
        );
        return (
          <DynamicLinkedDropDowns
            key={control.controlId}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            label={control.label}
            config={linkedDropDownsConfig}
            value={answer}
            visible={visible}
            disabled={disabled}
            onChange={(controlId, controlTypeId, value, isValid) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                linkedDropDownsConfig.required ?? false,
                null,
                null,
              )
            }
            showError={props.showErrors}
          />
        );
      case ControlTypes.TIP:
        const tipConfig: TipConfig = JSON.parse(control.config!);

        return (
          <DynamicTip
            key={control.controlId}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={tipConfig}
            value={answer}
            visible={visible}
          />
        );
      case ControlTypes.AUTOCOMPLETE:
        let autocompleteConfig: AutocompleteConfig = {};

        try {
          autocompleteConfig = JSON.parse(control.config!);
        } catch (err) {
          console.error(
            'Invalid config for autocomplete ' + control.controlId?.toString(),
          );
        }

        let autofill = undefined;
        if (props.controlHash)
          autofill = conditionsHelper.getAutofillCondition(
            control,
            props.conditions ?? [],
            props.answers,
            props.controlHash!,
          );

        let defaultValue = undefined;
        if (answer == undefined && autocompleteConfig.defaultSourceKey)
          defaultValue = getDefaultValue(
            autocompleteConfig.defaultSourceKey,
            props.submissionId,
          );

        let disabledBasicTrainee = false;
        if (
          defaultValue &&
          autocompleteConfig.defaultSourceKey === DEFAULT_SOURCE_KEY.TRAINEE
        )
          disabledBasicTrainee = true;

        return (
          <DynamicAutocomplete
            submissionId={props.submissionId}
            key={control.controlId}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={autocompleteConfig}
            navigation={props.navigation}
            label={control.label}
            value={answer}
            visible={visible}
            disabled={disabled || disabledBasicTrainee}
            onChange={(
              controlId,
              controlTypeId,
              value,
              isValid,
              metaDataKey,
              metaDataValue,
            ) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                autocompleteConfig.required ?? false,
                metaDataKey,
                metaDataValue,
              )
            }
            showError={props.showErrors}
            onFocus={() => setFocusedControlId(control.controlId)}
            focusedControlId={focusedControlId}
            autofill={autofill}
            defaultValue={defaultValue}
          />
        );
      case ControlTypes.AUTOCOMPLETE_EXTENDED:
        let autocompleteExtendedConfig: AutocompleteExtendedConfig = {};

        try {
          autocompleteExtendedConfig = JSON.parse(control.config!);
        } catch (err) {
          console.error(
            'Invalid config for autocomplete ' + control.controlId?.toString(),
          );
        }

        let autofillExtended = undefined;
        if (props.controlHash)
          autofillExtended = conditionsHelper.getAutofillCondition(
            control,
            props.conditions ?? [],
            props.answers,
            props.controlHash!,
          );

        if (
          answer == undefined &&
          autocompleteExtendedConfig.defaultSourceKey
        ) {
          let metaDataKey: string | null = null;
          if (
            autocompleteExtendedConfig.hasMetaData &&
            autocompleteExtendedConfig.metaDataKey
          )
            metaDataKey = autocompleteExtendedConfig.metaDataKey;

          answer = setDefault(
            answer,
            autocompleteExtendedConfig.defaultSourceKey,
            control,
            metaDataKey,
          );
        }

        return (
          <DynamicAutocompleteExtended
            submissionId={props.submissionId}
            key={control.controlId}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={autocompleteExtendedConfig}
            navigation={props.navigation}
            label={control.label}
            value={answer}
            visible={visible}
            disabled={disabled}
            onChange={(
              controlId,
              controlTypeId,
              value,
              isValid,
              metaDataKey,
              metadataValue,
            ) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                autocompleteExtendedConfig.required ?? false,
                metaDataKey,
                metadataValue,
              )
            }
            onBlur={() => setScrollEnabled(true)}
            showError={props.showErrors}
            autofill={autofillExtended}
          />
        );
      case ControlTypes.RISK_RATING:
        const riskRatingConfig: RiskRatingConfig = JSON.parse(control.config!);
        return (
          <DynamicRiskRating
            key={control.controlId}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            label={control.label}
            config={riskRatingConfig}
            value={answer}
            visible={visible}
            disabled={disabled}
            onChange={(controlId, controlTypeId, value, isValid) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                riskRatingConfig.required ?? false,
                null,
                null,
              )
            }
            showError={props.showErrors}
            externalView={props.externalView}
          />
        );
      case ControlTypes.RADIO:
        let radioConfig: RadioConfig | undefined = undefined;

        try {
          radioConfig = JSON.parse(control.config!);
        } catch (err) {
          console.error(
            'Invalid config for segment ' + control.controlId?.toString(),
          );
        }

        return (
          <DynamicRadioControl
            key={control.controlId}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={radioConfig}
            label={control.label}
            value={answer}
            visible={visible}
            disabled={disabled}
            onChange={(
              controlId,
              controlTypeId,
              value,
              isValid,
              metaDataKey,
              metadataValue,
            ) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                radioConfig?.required ?? false,
                metaDataKey,
                metadataValue,
              )
            }
          />
        );
      case ControlTypes.CHECKBOXGROUP:
        let checkGroupConfig: CheckboxGroupConfig | undefined = undefined;
        try {
          checkGroupConfig = JSON.parse(control.config!);
        } catch (err) {
          console.error(
            'Invalid config for checkbox group ' +
              control.controlId?.toString(),
          );
        }

        return (
          <DynamicCheckBoxGroup
            key={control.controlId}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={checkGroupConfig}
            label={control.label}
            value={answer}
            visible={visible}
            disabled={disabled}
            onChange={(
              controlId,
              controlTypeId,
              value,
              isValid,
              metaDataKey,
              metaDataValue,
            ) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                checkGroupConfig?.required ?? false,
                metaDataKey,
                metaDataValue,
              )
            }
            showError={props.showErrors}
          />
        );
      case ControlTypes.PEOPLE_LIST:
        let peopleListConfig: PeopleListConfig | undefined = undefined;

        try {
          if (control.config) peopleListConfig = JSON.parse(control.config);
        } catch (err) {
          console.error(
            'Invalid config for people list ' + control.controlId?.toString(),
          );
        }

        return (
          <DynamicPeopleList
            key={control.controlId}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={peopleListConfig}
            label={control.label}
            value={answer}
            onChange={(controlId, controlTypeId, value, isValid) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                peopleListConfig?.required ?? false,
                null,
                null,
              )
            }
            visible={visible}
            disabled={disabled}
            showError={props.showErrors}
          />
        );
      case ControlTypes.EIDA_DESCRIPTION:
        let eidaDescriptionConfig: EidaDescriptionConfig | undefined =
          undefined;

        try {
          eidaDescriptionConfig = JSON.parse(control.config!);
        } catch (err) {
          console.error(
            'Invalid config for eida description ' +
              control.controlId?.toString(),
          );
        }

        return (
          <DynamicEidaDescription
            key={control.controlId}
            label={control.label}
            controlId={control.controlId!}
            controlTypeId={control.controlTypeId}
            config={eidaDescriptionConfig}
            value={answer}
            visible={visible}
            disabled={disabled}
            onChange={(controlId, controlTypeId, value, isValid) =>
              onChange(
                controlId,
                controlTypeId,
                value,
                isValid,
                eidaDescriptionConfig?.required ?? false,
                null,
                null,
              )
            }
            showError={props.showErrors}
          />
        );
      case ControlTypes.LINK_BUTTON:
        let linkButtonConfig: LinkButtonConfig | null = null;

        try {
          linkButtonConfig = JSON.parse(control.config!);
        } catch (err) {
          console.error(
            'Invalid config for link button ' + control.controlId?.toString(),
          );
        }

        if (linkButtonConfig && linkButtonConfig.href)
          return (
            <DynamicLinkButton
              key={control.controlId}
              controlId={control.controlId!}
              controlTypeId={control.controlTypeId}
              config={linkButtonConfig}
              visible={visible}
              disabled={disabled}
            />
          );
        else return <></>;
      case ControlTypes.LINK_TEXT:
        let linkTextConfig: LinkTextConfig | null = null;

        try {
          linkTextConfig = JSON.parse(control.config!);
        } catch (err) {
          console.error(
            'Invalid config for link text ' + control.controlId?.toString(),
          );
        }

        if (linkTextConfig && linkTextConfig.href && linkTextConfig.linkText)
          return (
            <DynamicLinkText
              key={control.controlId}
              controlId={control.controlId!}
              controlTypeId={control.controlTypeId}
              config={linkTextConfig}
              visible={visible}
            />
          );
        else return <></>;
      default:
        return <React.Fragment key={control.controlId}></React.Fragment>;
    }
  };

  const scrollRef = useRef<ScrollView | null>(null);
  const scrollTo = (position: number) => {
    if (scrollRef.current)
      scrollRef.current.scrollTo({y: position, animated: true});
  };
  const scrollToBottom = () => {
    if (scrollRef.current)
      scrollRef.current.scrollToEnd({animated: true});
  };

  const controls: React.ReactElement[] = [];
  if (props.page && props.page.controls)
    for (let i = 0; i < props.page.controls.length; i++) {
      let control = props.page.controls![i];
      try {
        controls.push(getControl(control, controlHash));
      } catch (err) {
        console.log(control);
        console.log(err);
        console.error('bad contrl');
      }
    }

  const [scrollEnabled, setScrollEnabled] = useState(true);

  return (
    <ScrollView
      ref={ref => {
        scrollRef.current = ref;
      }}
      scrollEnabled={scrollEnabled}
      style={DynamicPageStyleSheet.body}>
      <View
        onPointerDown={() => setFocusedControlId(undefined)}
        style={DynamicPageStyleSheet.controlsContainer}>
        {controls}
      </View>
      {props.formVersion != null && (
        <View
          style={DynamicPageStyleSheet.formVersionContainer}
          pointerEvents='none'>
          <Text style={DynamicPageStyleSheet.formVersionText}>
            Form Version: {props.formVersion}
          </Text>
        </View>
      )}
    </ScrollView>
  );
};

export default DynamicPage;
